import React, { useEffect, useState } from 'react';
import { useData } from '../../lib/DataContext.jsx';
import { fmt, fmtInt, bandColor, fmtCr, TOOL_COLOR } from '../../lib/format.js';
import { X, Users, MapPin, Wallet, ClipboardCheck } from 'lucide-react';
import { BandPill, Chip } from './kits.jsx';

const TOOL_COLOR_MAP = {
  'H&H': '#2e7cbf',
  NRM: '#04294f',
  SDLE: '#e23d28',
  PoE: '#9b2d20',
};

/**
 * BeneficiaryModal — click any project code and see its beneficiary breakdown.
 * Displays: total responses, tool split donut, top respondent categories,
 *           villages covered, project meta.
 */
export default function BeneficiaryModal({ code, onClose }) {
  const { data, beneficiaries } = useData();
  const [meta, setMeta] = useState(null);
  const [ben, setBen] = useState(null);

  useEffect(() => {
    if (!code || !data) return;
    const p = data.allProjects.find((x) => x.code === code);
    setMeta(p);
    if (beneficiaries) {
      setBen(beneficiaries.projects[code] || null);
    }
  }, [code, data, beneficiaries]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!code || !meta) return null;

  const legend = beneficiaries?.legend || {};
  const total = ben?.total || 0;
  const tools = ben?.byTool || {};
  const cats = ben?.topCategories || {};
  const toolMax = Math.max(1, ...Object.values(tools));

  return (
    <div
      onClick={onClose}
      data-testid="beneficiary-modal-backdrop"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(4,26,54,0.55)',
        backdropFilter: 'blur(6px)', zIndex: 100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="beneficiary-modal"
        style={{
          background: 'var(--surface)', borderRadius: 16, width: '100%', maxWidth: 780,
          maxHeight: '90vh', overflow: 'auto', boxShadow: 'var(--shadow-lg)',
          border: '1px solid var(--line)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px', borderBottom: '1px solid var(--line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14,
          background: 'linear-gradient(140deg, #042a54, #004c8f)', color: '#fff',
          borderRadius: '16px 16px 0 0',
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, opacity: .75, textTransform: 'uppercase', letterSpacing: '.09em' }}>
              Beneficiary drill-down · {meta.code}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, lineHeight: 1.3 }}>
              {meta.partner || 'Unknown Partner'}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={pillOnDark(meta.type === 'HRDP' ? '#7cb9e8' : '#ffb0a3')}>{meta.type}</span>
              <span style={pillOnDark('#ffffff')}>
                <MapPin size={11} style={{ marginRight: 4 }} /> {meta.state || '—'} · {meta.region}
              </span>
              <span style={pillOnDark('#ffe0d6')}>
                <Wallet size={11} style={{ marginRight: 4 }} /> {meta.budgetCr > 0 ? fmtCr(meta.budgetCr) : 'Budget n/a'}
              </span>
              {ben && ben.researchAgency && (
                <span style={pillOnDark('#c6e0b4')} title={`Data collection agency · ${ben.studyType || ''} · FY ${ben.fyOfStudy || ''}`}>
                  <ClipboardCheck size={11} style={{ marginRight: 4 }} />
                  Assessed by {ben.researchAgency}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="tabular" style={{ fontSize: 26, fontWeight: 800, lineHeight: 1 }}>{fmt(meta.overall, 2)}</span>
              <BandPill band={meta.band} />
            </div>
            <button onClick={onClose} data-testid="beneficiary-modal-close" style={{
              background: '#ffffff22', border: '1px solid #ffffff44', color: '#fff',
              padding: '4px 8px', borderRadius: 8, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600,
            }}>
              <X size={12} /> Close
            </button>
          </div>
        </div>

        {/* Body */}
        {ben ? (
          <div style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Big number */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 20, alignItems: 'center',
              padding: 14, background: 'var(--surface-2)', border: '1px solid var(--line)',
              borderRadius: 12,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 76, height: 76, borderRadius: 20,
                background: 'linear-gradient(140deg, var(--primary), var(--primary-2))',
                color: '#fff',
              }}>
                <Users size={30} />
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em' }}>
                  Beneficiaries surveyed
                </div>
                <div className="tabular" style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: 'var(--ink)' }}>
                  {fmtInt(total)}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>
                  across <b>{ben.villagesCount}</b> village-region{ben.villagesCount === 1 ? '' : 's'} · {Object.keys(tools).length} tools
                </div>
              </div>
            </div>

            {/* Tool split */}
            <div>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>
                Response distribution by tool
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {['H&H', 'NRM', 'PoE', 'SDLE'].map((t) => {
                  const v = tools[t] || 0;
                  const pct = v ? (v / toolMax) * 100 : 0;
                  return (
                    <div key={t} style={{ display: 'grid', gridTemplateColumns: '52px 1fr 90px', gap: 10, alignItems: 'center' }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: TOOL_COLOR_MAP[t] }}>{t}</div>
                      <div style={{ height: 14, background: 'var(--line-2)', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                        <div className="anim-grow" style={{
                          height: '100%', width: `${pct}%`,
                          background: TOOL_COLOR_MAP[t], borderRadius: 4,
                        }} />
                      </div>
                      <div className="tabular" style={{ textAlign: 'right', fontSize: 12, fontWeight: 700, color: v ? 'var(--ink)' : 'var(--ink-4)' }}>
                        {v ? `${fmtInt(v)}` : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
              {ben.byTool.SDLE > 0 && (
                <div style={{ marginTop: 6, fontSize: 10.5, color: 'var(--ink-4)' }}>
                  SDLE count is indicator-response granularity — higher than headcount.
                </div>
              )}
            </div>

            {/* Categories */}
            {Object.keys(cats).length > 0 && (
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.09em', marginBottom: 8 }}>
                  Top respondent categories
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.entries(cats).map(([k, v]) => (
                    <div key={k}
                         title={legend[k] ? `${legend[k]} · ${v} responses` : `${v} responses`}
                         style={{
                           display: 'inline-flex', alignItems: 'center', gap: 6,
                           background: 'var(--primary-soft)', color: 'var(--primary)',
                           padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                         }}>
                      <span>{legend[k] || k}</span>
                      <span className="tabular" style={{ background: 'var(--primary)', color: '#fff', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 700 }}>
                        {fmtInt(v)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Meta table */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 8, padding: 12, background: 'var(--surface-2)',
              border: '1px solid var(--line)', borderRadius: 10,
            }}>
              <MetaCell label="Interventions" value={meta.interventions?.length || '—'} />
              <MetaCell label="Villages served" value={ben.villagesCount} />
              <MetaCell label="Thematic" value={meta.thematic || '—'} />
              <MetaCell label="Zone" value={meta.zone || '—'} />
            </div>
          </div>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 14 }} data-testid="beneficiary-modal-empty">
            No beneficiary data available for {code}.
          </div>
        )}
      </div>
    </div>
  );
}

function MetaCell({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-4)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }} className="tabular">{value}</div>
    </div>
  );
}

// Pill style optimised for the navy modal header — always readable on dark bg.
function pillOnDark(color) {
  return {
    display: 'inline-flex', alignItems: 'center',
    background: 'rgba(255,255,255,0.14)',
    border: `1px solid ${color}55`,
    color, fontWeight: 700, fontSize: 11,
    padding: '3px 9px', borderRadius: 999,
    whiteSpace: 'nowrap',
  };
}
